import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import ApproveModal from '@/components/Modal/ApproveModal';
import useFilter from '@/hooks/useFilter';
import { IDeleteResponse, IFilterSideBarForm } from '@/interfaces/common.model';
import {
  IPagination,
  ITableData,
  ITeamLeavePageState,
  ITeamLeaveResponse,
  ITeamLeaveSort,
  ITeamLeaveState,
} from '@/interfaces/leave.model';
import { ILeaveSettingResponse } from '@/interfaces/leaveSetting.model';
import {
  setDateFilter,
  setUpdateFilterStates,
} from '@/redux/features/UserSlice';
import { RootState } from '@/redux/store';
import { calculateDaysDifference } from '@/utility/CommonFunctions';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
const TeamLeave = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ITeamLeaveState>({
    leaveList: [],
    leaveTypeList: [],
  });
  const [selectedLeave, setSelectedLeave] = useState<ITableData | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(
    null,
  );
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const date = useSelector((state: RootState) => state?.user?.months);
  const dispatch = useDispatch();
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );
  const [pagination, setPagination] = useState<IPagination>({
    currentPage: 1,
    count: 0,
    totalPages: 0,
    searchQuery: '',
  });
  const [pageStates, setPageStates] = useState<ITeamLeavePageState>({
    tableLoader: false,
    sortByDate: [
      moment().startOf('month').format('YYYY-MM-DD'),
      moment().endOf('month').format('YYYY-MM-DD'),
    ],
    leaveStatus: '',
    leaveType: '',
  });

  const tableHeading: ITableColumn<ITableData>[] = [
    { value: 'index', label: '#' },
    { value: 'empNameWithImage', label: 'Emp. Name' },
    { value: 'employeeCode', label: 'Emp. Code' },
    { value: 'employeeDept', label: 'Emp. Dept.' },
    { value: 'officeName', label: 'Office' },
    { value: 'leaveType', label: 'Leave Type' },
    { value: 'appliedOn', label: 'Applied On' },
    { value: 'fromDate', label: 'From' },
    { value: 'toDate', label: 'To' },
    { value: 'days', label: 'Days' },
    { value: 'leaveStatus', label: 'Status' },
    { value: 'unpaid', label: 'Unpaid Leave' },
    { value: 'requestAction', label: 'Request Action' },
  ];
  useEffect(() => {
    getLeaveRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    date,
    pagination?.currentPage,
    pagination?.searchQuery,
    filterDetails?.leaveStatus,
    filterDetails?.leaveType,
    filterDetails?.pageSize,
  ]);
  useEffect(() => {
    getLeaveSetting();
  }, []);
  const getLeaveRequest = async () => {
    try {
      const filter = {
        search: pagination?.searchQuery,
        pageSize: filterDetails?.pageSize,
        pageNumber: filterDetails?.currentPage,
        startDate: date?.[0],
        endDate: date?.[1],
        leaveType: filterDetails?.leaveType,
        status: filterDetails?.leaveStatus,
      };
      setPageStates((pre) => ({
        ...pre,
        tableLoader: true,
      }));
      const API = API_URL + API_ENDPOINT?.getLeave + '?' + getFilterUrl(filter);
      const res: ITeamLeaveResponse = await callApi(API, {
        method: 'GET',
      });

      if (res?.status === true) {
        const newData: ITableData[] = res?.data?.leave?.map((item, index) => {
          const days =
            Number(
              calculateDaysDifference(item?.startDate, item?.endDate, 'number'),
            ) || 0;
          const result =
            item?.leaveDuration == 'firstHalf' ||
              item?.leaveDuration == 'secondHalf'
              ? days * 0.5
              : days;

          const isUnpaid = Boolean(
            item?.unPaidLeave &&
            (item?.unPaidLeave?.leaveId === item?.id ||
              Boolean(item?.unPaidLeave?.id)),
          );

          return {
            ...item,
            index: index + 1,
            employeeCode: item?.user?.employeeCode,
            empNameWithImage:
              item?.user?.name + '##' + item?.user?.profilePhoto,
            employeeDept: item?.user?.department?.name ?? '--',
            officeName: item?.user?.site?.name ?? '--',
            siteName: item?.user?.usersites?.[0]?.site?.name ?? '',
            appliedOn: moment(item?.createdAt).format('DD/MM/YYYY'),
            fromDate: moment(item.startDate).format('DD/MM/YYYY'),
            toDate: moment(item.endDate).format('DD/MM/YYYY'),
            days: result ? Number(result) : '--',
            leaveStatus: item?.status ?? '--',
            unPaidLeave: item?.unPaidLeave ?? null,
            unpaid: isUnpaid ? 'Yes' : 'No',
          };
        });
        setState((pre) => ({
          ...pre,
          leaveList: newData,
        }));
        setPagination((pre) => ({
          ...pre,
          count: res?.data?.pageInfo?.total,
          totalPages: res?.data?.pageInfo?.totalPages,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: false,
      }));
    }
  };

  const getLeaveSetting = async () => {
    try {
      const Url = API_URL + API_ENDPOINT?.getLeaveSetting + '?all=true';
      const res: ILeaveSettingResponse = await callApi(Url, {
        method: 'GET',
      });
      if (res?.status === true) {
        setState((pre) => ({
          ...pre,
          leaveTypeList: res?.data,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    }
  };

  const dateChangeHandler = (Dates: Date[] | null) => {
    let formattedDate = null;
    if (Dates && Dates.length === 2) {
      const startDate = new Date(
        Dates[0].getTime() - Dates[0].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      const endDate = new Date(
        Dates[1].getTime() - Dates[1].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      formattedDate = [startDate, endDate];
    }
    if (formattedDate !== null) {
      dispatch(setDateFilter(formattedDate));
      setPageStates((prevState) => ({
        ...prevState,
        sortByDate: formattedDate,
      }));
    } else {
      setPageStates((prevState) => ({ ...prevState, sortByDate: [] }));
    }
  };

  const { handleChangePageSize, handleSearchChange, pageChangeHandler } =
    useFilter(pagination, setPagination);

  const actionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view',
      clickHandler: (data: ITableData) =>
        navigate(`${RoutesLink?.leaveTeamDetail}/${data?.id}`, {
          state: { type: 'teamLeaveRequest' },
        }),
    },
  ];
  const handleData = (data: Partial<IFilterSideBarForm> | null) => {
    setPageStates((pre) => ({
      ...pre,
      leaveStatus: data?.leaveStatus ?? '',
      leaveType: data?.leaveType ?? '',
    }));
    dispatch(
      setUpdateFilterStates({
        ...data,
        leaveStatus: data?.leaveStatus ?? '',
        leaveType: data?.leaveType ?? '',
      }),
    );
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const TeamLeaveSort: ITeamLeaveSort = {
    leaveStatus: [
      { value: '', label: 'Select Leave Status' },
      { value: 1, label: 'Approved' },
      { value: 0, label: 'Pending' },
      { value: 2, label: 'Rejected' },
    ],
    leaveType: state?.leaveTypeList,
  };

  const handleApprove = (data?: ITableData) => {
    if (data) {
      setSelectedLeave(data);
      setActionType('approve');
      setIsConfirmModalOpen(true);
    }
  };

  const handleReject = (data?: ITableData) => {
    if (data) {
      setSelectedLeave(data);
      setActionType('reject');
      setIsConfirmModalOpen(true);
    }
  };

  const handleApproveReject = async (type: string, reason?: string) => {
    if (!selectedLeave?.id) {
      showToastNotification('danger', 'Please select a request');
      return;
    }
    setIsUpdating(true);
    const isHalfDay =
      selectedLeave?.leaveDuration == 'firstHalf' ||
      selectedLeave?.leaveDuration == 'secondHalf';
    let newStatus;
    if (type == 'approve') {
      newStatus = 1;
    } else if (type == 'reject') {
      newStatus = 2;
    }
    const submitData = {
      type: 'leave',
      id: selectedLeave?.id,
      ...(isHalfDay ? { isHalfDayStatus: newStatus } : { status: newStatus }),
      status: newStatus,
      ...(reason && { reason }),
    };
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.userStatusUpdate,
        {
          method: 'POST',
          data: submitData,
        },
      );
      if (response?.status) {
        setIsConfirmModalOpen(false);
        showToastNotification('success', response?.message);
        getLeaveRequest();
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsUpdating(false);
    }
  };

  const submitConfirm = (actionType?: string, reason?: string) => {
    if (actionType === 'approve') {
      handleApproveReject('approve');
    } else if (actionType === 'reject') {
      handleApproveReject('reject', reason);
    }
  };

  return (
    <>
      <Layout>
        <div className="content">
          <MainHeading
            title="Team Leave Request"
            dateChangeHandler={dateChangeHandler}
            showDefaultDate={true}
            pageState={{
              sortByDate: date,
            }}
          />
          <CardLayout isGrid={tableView === 'grid'}>
            <FilterComponent
              searchPlaceHolder="Search"
              handleSearch={handleSearchChange}
              filterArray={{
                isShow: true,
              }}
              isGrid={false}
              sortHandler={handleData}
              TeamLeaveSort={TeamLeaveSort}
            />
            <CommonTable
              tableHeading={
                state?.leaveList?.some(
                  (item) => Number(item?.leaveStatus) === 0,
                )
                  ? tableHeading
                  : tableHeading?.filter(
                    (item) => item?.value !== 'requestAction',
                  )
              }
              tableData={state?.leaveList}
              ActionData={actionData}
              pageSize={filterDetails?.pageSize}
              totalPages={pagination?.totalPages}
              currentPage={filterDetails?.currentPage}
              count={pagination?.count}
              isLoading={pageStates?.tableLoader}
              handleChangePageSize={handleChangePageSize}
              pageChangeHandler={pageChangeHandler}
              handleApprove={handleApprove}
              handleReject={handleReject}
            />
          </CardLayout>
        </div>
      </Layout>
      <ApproveModal
        isOpen={isConfirmModalOpen}
        isLoading={isUpdating}
        submitHandler={submitConfirm}
        toggle={() => setIsConfirmModalOpen(false)}
        actionType={actionType ?? ''}
      />
    </>
  );
};

export default TeamLeave;
